import { Chain, connectorsForWallets } from "@rainbow-me/rainbowkit";

import {
  bscTestnet,
  sepolia,
  polygonMumbai,
  avalancheFuji,
  arbitrumGoerli,
  mainnet,
  bsc,
  arbitrum,
  polygon,
  avalanche,
  baseSepolia,
  base,
  optimismSepolia,
  optimism,
} from "wagmi/chains";

import {
  rainbowWallet,
  walletConnectWallet,
  metaMaskWallet,
  rabbyWallet,
  coinbaseWallet,
  trustWallet,
} from "@rainbow-me/rainbowkit/wallets";
import { defineChain } from 'viem';

import { Web3AuthConnector } from "@web3auth/web3auth-wagmi-connector";
import { Web3Auth } from "@web3auth/modal";
import { EthereumPrivateKeyProvider } from "@web3auth/ethereum-provider";
import { CHAIN_NAMESPACES, UX_MODE, WEB3AUTH_NETWORK } from "@web3auth/base";

import { appConfig } from "../../config";
import { createConfig, http } from "wagmi";
import { getRpcUrlHttp, getSupportedChainIds } from "./constants/chains";
import { createClient } from "viem";
import { Wallet, WalletDetailsParams } from "@rainbow-me/rainbowkit";
import { createConnector as createWagmiConnector } from "wagmi";

import emailWalletIcon from "../../assets/images/emailwallet.png";

const projectId = appConfig.walletConnect.projectId;
const supportChainIds = getSupportedChainIds();
const clientId = appConfig.web3Auth.clientId; // Web3Auth client id
const web3Enabled = appConfig.web3Auth.enabled;
const isMainnetEnabled = appConfig.web3Auth.isMainnet;

// Define abstract chains using `createChain`
export const abstractMainnet = defineChain({
  id: 2741,
  name: 'Abstract',
  network: 'abstract',
  nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
  rpcUrls: { default: { http: ['https://api.mainnet.abs.xyz'] } },
  blockExplorers: { default: { name: 'Abstract', url: 'https://abscan.org/' } },
});

export const abstractTestnet = defineChain({
  id: 11124,
  name: 'Abstract Testnet',
  network: 'abstract testnet',
  nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
  rpcUrls: { default: { http: ['https://api.testnet.abs.xyz'] } },
  blockExplorers: { default: { name: 'Abstract Sepolia', url: 'https://sepolia.abscan.org/' } },
});

export const CHAINS = [
  bscTestnet,
  sepolia,
  polygonMumbai,
  avalancheFuji,
  arbitrumGoerli,
  mainnet,
  bsc,
  arbitrum,
  polygon,
  avalanche,
  base,
  baseSepolia,
  abstractMainnet,
  abstractTestnet,
  optimismSepolia,
  optimism,
].filter((chain: Chain) => supportChainIds.includes(chain?.id));

const chains: [Chain, ...Chain[]] = [CHAINS[0], ...CHAINS];

const chainConfigTestnet = {
  chainNamespace: CHAIN_NAMESPACES.EIP155,
  chainId: "0xaa36a7",
  rpcTarget: "https://rpc.ankr.com/eth_sepolia",
  // Avoid using public rpcTarget in production.
  // Use services like Infura, Quicknode etc
  displayName: "Ethereum Sepolia Testnet",
  blockExplorerUrl: "https://sepolia.etherscan.io",
  ticker: "ETH",
  tickerName: "Ethereum",
  logo: "https://cryptologos.cc/logos/ethereum-eth-logo.png",
};

const chainConfigMainnet = {
  chainNamespace: CHAIN_NAMESPACES.EIP155,
  chainId: "0x1",
  rpcTarget: "https://rpc.ankr.com/eth",
  // Avoid using public rpcTarget in production.
  // Use services like Infura, Quicknode etc
  displayName: "Ethereum Mainnet",
  blockExplorerUrl: "https://etherscan.io",
  ticker: "ETH",
  tickerName: "Ethereum",
  logo: "https://cryptologos.cc/logos/ethereum-eth-logo.png",
};

const chainConfig = isMainnetEnabled ? chainConfigMainnet : chainConfigTestnet;

const privateKeyProvider = new EthereumPrivateKeyProvider({
  config: { chainConfig },
});

const web3AuthNetwork = isMainnetEnabled
  ? WEB3AUTH_NETWORK.SAPPHIRE_MAINNET
  : WEB3AUTH_NETWORK.SAPPHIRE_DEVNET;

const web3AuthInstance = new Web3Auth({
  clientId,
  web3AuthNetwork: web3AuthNetwork,
  privateKeyProvider,
  uiConfig: {
    mode: "dark",
    useLogoLoader: true,
    logoLight: "https://cryptologos.cc/logos/ethereum-eth-logo.png",
    logoDark: "https://cryptologos.cc/logos/ethereum-eth-logo.png",
    defaultLanguage: "en",
    theme: {
      primary: "#768729",
    },
    uxMode: UX_MODE.REDIRECT,
    modalZIndex: "2147483647",
  },
});

export const rainbowWeb3AuthConnector = (): Wallet => ({
  id: "web3auth",
  name: "Email Wallet",
  rdns: "web3auth",
  iconUrl: emailWalletIcon,
  iconBackground: "none",
  installed: true,
  downloadUrls: {},
  createConnector: (walletDetails: WalletDetailsParams) =>
    createWagmiConnector((config) => ({
      ...Web3AuthConnector({
        web3AuthInstance,
      })(config),
      ...walletDetails,
    })),
});

const walletGroups = [
  {
    groupName: "Recommended",
    wallets: [metaMaskWallet, walletConnectWallet, rabbyWallet],
  },
  {
    groupName: "Popular",
    wallets: [coinbaseWallet, rainbowWallet, trustWallet],
  },
];

if (web3Enabled) {
  walletGroups.splice(1, 0, {
    groupName: "Don't have a web3 wallet?",
    wallets: [rainbowWeb3AuthConnector],
  });
}

const connectors = connectorsForWallets(walletGroups, {
  appName: "GainsPad",
  projectId: projectId,
});

export const config = createConfig({
  connectors,
  chains,
  client({ chain }) {
    return createClient({ chain, transport: http(getRpcUrlHttp(chain.id)) });
  },
});
